import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
	ChevronLeft,
	ChevronRight,
	Download,
	Eye,
	EyeOff,
	Grid,
	Menu,
	Play,
	RotateCw,
	X,
	ZoomIn,
	ZoomOut
} from 'lucide-react';

export const LightBoxModal = ({eventDetails, isOpen, onClose, mediaType, media = [], startIndex = 0}) => {

	const [currentIndex, setCurrentIndex] = useState(0);
	const [zoom, setZoom] = useState(1);
	const [rotation, setRotation] = useState(0);
	const [showThumbnails, setShowThumbnails] = useState(true);

	let currentMedia = null;
	let isCurrentVideo = false;

	const [viewMode, setViewMode] = useState('slider');

	const [isDragging, setIsDragging] = useState(false);
	const [position, setPosition] = useState({x: 0, y: 0});
	const [dragStart, setDragStart] = useState({x: 0, y: 0});
	const [startPosition, setStartPosition] = useState({x: 0, y: 0});

	const [isTransitioning, setIsTransitioning] = useState(false);
	const [showMobileMenu, setShowMobileMenu] = useState(false);

	const prevViewModeRef = useRef();
	const thumbnailContainerRef = useRef(null);
	const imageRef = useRef(null);
	const containerRef = useRef(null);

	const closeLightbox = useCallback(() => {
		onClose();
		setZoom(1);
		setRotation(0);
		setPosition({x: 0, y: 0});
		setCurrentIndex(0);
		setShowMobileMenu(false);
		setViewMode('slider');
	}, [onClose]);

	const goToNext = useCallback(() => {
		setIsTransitioning(true);
		setTimeout(() => {
			setCurrentIndex((prev) => (prev + 1) % media?.length);
			resetImageState();
			setIsTransitioning(false);
		}, 150);
	}, [media?.length]);

	const goToPrev = useCallback(() => {
		setIsTransitioning(true);
		setTimeout(() => {
			setCurrentIndex((prev) => (prev - 1 + media?.length) % media?.length);
			resetImageState();
			setIsTransitioning(false);
		}, 150);
	}, [media?.length]);

	const resetImageState = () => {
		setZoom(1);
		setRotation(0);
		setPosition({x: 0, y: 0});
	};

	const handleZoomIn = () => {
		setZoom((prev) => Math.min(prev + 0.5, 4));
	};

	const handleZoomOut = () => {
		setZoom((prev) => Math.max(prev - 0.5, 0.5));
	};

	const handleRotate = () => {
		setRotation((prev) => (prev + 90) % 360);
	};

	const handleDownload = async () => {
		const currentMedia = media[currentIndex];
		if (mediaType === 'image') {
			try {
				// Note: This fetch-based download might be blocked by CORS for cross-origin images.
				const response = await fetch(currentMedia.url);
				const blob = await response.blob();
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				link.download = `${currentMedia.title}.jpg`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(url);
			} catch (error) {
				console.error('Download failed:', error);
			}
		}
	};

	const scrollThumbnails = (direction) => {
		if (thumbnailContainerRef.current) {
			const scrollAmount = 200;
			thumbnailContainerRef.current.scrollBy({
				left: direction === 'left' ? -scrollAmount : scrollAmount,
				behavior: 'smooth'
			});
		}
	};

	const handleMouseDown = (e) => {
		if (zoom > 1 && mediaType === 'image') {
			e.preventDefault();
			setIsDragging(true);
			setDragStart({x: e.clientX, y: e.clientY});
			setStartPosition({x: position.x, y: position.y});
		}
	};

	const handleMouseMove = (e) => {
		if (isDragging && zoom > 1) {
			const deltaX = e.clientX - dragStart.x;
			const deltaY = e.clientY - dragStart.y;
			setPosition({
				x: startPosition.x + deltaX,
				y: startPosition.y + deltaY
			});
		}
	};

	const handleMouseUp = () => {
		setIsDragging(false);
	};

	const handleTouchStart = (e) => {
		if (zoom > 1 && mediaType === 'image') {
			const touch = e.touches[0];
			setIsDragging(true);
			setDragStart({x: touch.clientX, y: touch.clientY});
			setStartPosition({x: position.x, y: position.y});
		}
	};

	const handleTouchMove = (e) => {
		if (isDragging && zoom > 1) {
			const touch = e.touches[0];
			const deltaX = touch.clientX - dragStart.x;
			const deltaY = touch.clientY - dragStart.y;
			setPosition({
				x: startPosition.x + deltaX,
				y: startPosition.y + deltaY
			});
		}
	};

	const handleTouchEnd = () => {
		setIsDragging(false);
	};

	const handleThumbnailClick = (index) => {
		setIsTransitioning(true);
		setTimeout(() => {
			setCurrentIndex(index);
			resetImageState();
			setIsTransitioning(false);
		}, 150);
	};

	// *** NEW HANDLER FOR VIEW MODE ***
	const handleToggleViewMode = () => {
		const newMode = viewMode === 'slider' ? 'grid' : 'slider';
		if (newMode === 'grid') {
			setCurrentIndex(0);
			resetImageState();
		}
		setViewMode(newMode);
	};

	useEffect(() => {
		const handleKeyDown = (e) => {
			if (!isOpen) return;
			switch (e.key) {
				case 'Escape':
					closeLightbox();
					break;
				case 'ArrowRight':
					goToNext();
					break;
				case 'ArrowLeft':
					goToPrev();
					break;
				default:
					break;
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, goToNext, goToPrev, closeLightbox]);

	useEffect(() => {
		setCurrentIndex(startIndex);
	}, [startIndex]);

	useEffect(() => {
		if (zoom <= 1) {
			setPosition({x: 0, y: 0});
		}
	}, [zoom]);

	useEffect(() => {
		console.log(`Media: ${JSON.stringify(media)} | Media Type: ${mediaType}`);
		if (showThumbnails && viewMode === 'slider' && thumbnailContainerRef.current) {

			// 1. Safety check: make sure media[currentIndex] exists
			if (!media[currentIndex]) return;

			// 2. Get the unique ID of the thumbnail we need
			const targetId = `lightbox-thumb-${media[currentIndex]._id}`;

			// 3. Find that element directly in the document
			const activeThumbnail = document.getElementById(targetId);
			const container = thumbnailContainerRef.current;

			if (activeThumbnail && container) {
				// 4. We found it! Now we do the *reliable* manual scroll calculation
				const justSwitchedFromGrid = prevViewModeRef.current === 'grid';

				const containerWidth = container.offsetWidth;
				const thumbnailOffsetLeft = activeThumbnail.offsetLeft;
				const thumbnailWidth = activeThumbnail.offsetWidth;

				const targetScrollLeft = thumbnailOffsetLeft - (containerWidth / 2) + (thumbnailWidth / 2);

				// 5. Scroll to the calculated position
				container.scrollTo({
					left: targetScrollLeft,
					// 'auto' (instant) if we just switched, 'smooth' for next/prev
					behavior: justSwitchedFromGrid ? 'auto' : 'smooth'
				});
			}
		}
	}, [currentIndex, showThumbnails, viewMode, media]);

	useEffect(() => {
		console.log(`Current Media: ${JSON.stringify(currentMedia)}`);
	}, [currentMedia]);

	if (!isOpen || !media || media?.length === 0) {
		return null;
	}

	currentMedia = media[currentIndex];
	isCurrentVideo = mediaType === 'video';

	console.log(`Current Media: `, currentMedia);
	console.log("Media Type:", mediaType);

	return (
		<div className="fixed inset-0 z-[100] bg-black bg-opacity-95 flex flex-col">
			{/* Top Controls */}
			<div className="flex items-center justify-between p-3 sm:p-4 bg-black bg-opacity-50">
				<div className="flex items-center gap-2">
					{
						viewMode === 'slider' && (
							<span className={`text-white font-custom-medium text-sm hidden sm:inline`}>
			                    {`${currentIndex + 1} / ${media?.length} - `}
			                </span>
						)
					}
					<span className={`text-white font-custom-medium text-sm`}>
	                   {eventDetails.title}
	                </span>
				</div>

				{/* Desktop Controls */}
				<div className="hidden sm:flex items-center gap-2">
					{!isCurrentVideo && viewMode === 'slider' && (
						<>
							<button
								onClick={handleZoomOut}
								disabled={zoom <= 0.5}
								className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								aria-label="Zoom Out"
							>
								<ZoomOut className="text-white font-custom-medium" size={20}/>
							</button>

							<span className="text-white font-custom-medium text-sm min-w-16 text-center">
		                        {Math.round(zoom * 100)}%
							</span>

							<button
								onClick={handleZoomIn}
								disabled={zoom >= 4}
								className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								aria-label="Zoom In"
							>
								<ZoomIn className="text-white font-custom-medium" size={20}/>
							</button>

							<div className="w-px h-6 bg-gray-600 mx-2"></div>

							<button
								onClick={handleRotate}
								className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors"
								title="Rotate"
								aria-label="Rotate"
							>
								<RotateCw className="text-white font-custom-medium" size={20}/>
							</button>

							<button
								onClick={handleDownload}
								className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors"
								title="Download"
								aria-label="Download"
							>
								<Download className="text-white font-custom-medium" size={20}/>
							</button>

							<div className="w-px h-6 bg-gray-600 mx-2"></div>
						</>
					)}

					<button
						onClick={handleToggleViewMode} // <-- UPDATED
						className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors"
						title={viewMode === 'slider' ? 'Grid View' : 'Slider View'}
						aria-label={viewMode === 'slider' ? 'Show Grid View' : 'Show Slider View'}
					>
						<Grid className="text-white font-custom-medium" size={20}/>
					</button>

					{viewMode === 'slider' && (
						<button
							onClick={() => setShowThumbnails(!showThumbnails)}
							className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors"
							title={showThumbnails ? 'Hide Thumbnails' : 'Show Thumbnails'}
							aria-label={showThumbnails ? 'Hide Thumbnails' : 'Show Thumbnails'}
						>
							{showThumbnails ? <EyeOff className="text-white font-custom-medium" size={20}/> :
								<Eye className="text-white font-custom-medium" size={20}/>}
						</button>
					)}

					<div className="w-px h-6 bg-gray-600 mx-2"></div>

					<button
						onClick={closeLightbox}
						className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors"
						aria-label="Close Lightbox"
					>
						<X className="text-white font-custom-medium" size={24}/>
					</button>
				</div>

				{/* Mobile Controls */}
				<div className="flex sm:hidden items-center gap-2">
					<button
						onClick={handleToggleViewMode} // <-- UPDATED
						className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors"
						aria-label={viewMode === 'slider' ? 'Show Grid View' : 'Show Slider View'}
					>
						<Grid className="text-white font-custom-medium" size={20}/>
					</button>

					<button
						onClick={closeLightbox}
						className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors"
						aria-label="Close Lightbox"
					>
						<X className="text-white font-custom-medium" size={24}/>
					</button>
				</div>
			</div>

			{/* Main Media Area */}
			{viewMode === 'slider' ? (
				<div className="flex-1 relative flex items-center justify-center overflow-hidden">
					{/* Navigation Arrows */}
					<button
						onClick={goToPrev}
						className="absolute left-2 sm:left-4 z-10 p-2 sm:p-3 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full transition-all"
						aria-label="Previous"
					>
						<ChevronLeft className="text-white font-custom-medium" size={24}/>
					</button>

					{/* Media Container */}
					<div
						ref={containerRef}
						className="relative select-none flex items-center justify-center"
						style={{
							cursor: zoom > 1 && !isCurrentVideo ? (isDragging ? 'grabbing' : 'grab') : 'default',
							width: '100%',
							height: '100%'
						}}
						onMouseDown={handleMouseDown}
						onMouseMove={handleMouseMove}
						onMouseUp={handleMouseUp}
						onMouseLeave={handleMouseUp}
						onTouchStart={handleTouchStart}
						onTouchMove={handleTouchMove}
						onTouchEnd={handleTouchEnd}
					>
						{isCurrentVideo ? (
							<iframe
								key={currentIndex}
								src={currentMedia.originalMediaUrl}
								title={eventDetails.title}
								className={`max-h-[60vh] sm:max-h-[70vh] max-w-[90vw] transition-opacity duration-300 ${
									isTransitioning ? 'opacity-0' : 'opacity-100'
								}`}
								style={{
									width: '90vw',
									height: '60vh',
									maxWidth: '1200px',
									maxHeight: '675px'
								}}
								frameBorder="0"
								allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
								allowFullScreen
							/>
						) : (
							<img
								ref={imageRef}
								key={currentIndex}
								src={currentMedia.originalMediaUrl}
								alt={eventDetails.title}
								className={`max-h-[60vh] sm:max-h-[70vh] max-w-[90vw] object-contain transition-all duration-300 ${
									isTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
								}`}
								style={{
									transform: `scale(${zoom}) rotate(${rotation}deg) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
									transformOrigin: 'center center'
								}}
								draggable="false"
							/>
						)}
					</div>

					<button
						onClick={goToNext}
						className="absolute right-2 sm:right-4 z-10 p-2 sm:p-3 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full transition-all"
						aria-label="Next"
					>
						<ChevronRight className="text-white font-custom-medium" size={24}/>
					</button>
				</div>
			) : (
				<div className={`flex-1 overflow-auto thin-scrollbar px-4 pb-4`}>
					<div
						className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 max-w-full">
						{media.map((item, index) => (
							<div
								key={item._id}
								onClick={() => {
									setCurrentIndex(index);
									setViewMode('slider');
									resetImageState();
								}}
								className={`relative overflow-hidden rounded-lg cursor-pointer group 
                                    aspect-auto min-h-[10rem] h-[10rem] max-h-[10rem]`}
							>
								{/*{mediaType === 'image' ? (*/}
								{/*	<img*/}
								{/*		src={item.thumbnailUrl}*/}
								{/*		alt={eventDetails.title}*/}
								{/*		className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"*/}
								{/*	/>*/}
								{/*) : (*/}
								{/*	<div*/}
								{/*		className="w-full h-full flex items-center justify-center bg-slate-800">*/}
								{/*		<Play*/}
								{/*			className="text-white font-custom-medium opacity-60 group-hover:opacity-100 transition-opacity"*/}
								{/*			size={32}/>*/}
								{/*	</div>*/}
								{/*)}*/}
								{/*<div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300"></div>*/}

								{/* UPDATED: Always render Image */}
								<img
									src={item.thumbnailUrl}
									alt={eventDetails.title}
									className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
								/>

								{/* UPDATED: Overlay Play button on hover if video */}
								{mediaType === 'video' && (
									<div
										className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300">
										<Play
											className="text-white font-custom-medium opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-110"
											size={40}
										/>
									</div>
								)}

								{/* Base hover darkening for non-videos (optional, or keep generic hover) */}
								{mediaType !== 'video' && (
									<div
										className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300"></div>
								)}
							</div>
						))}
					</div>
				</div>
			)}

			{/*/!* Thumbnails Section *!/*/}
			{/*<div*/}
			{/*	className={`bg-black bg-opacity-50 relative transition-all duration-300 ease-in-out*/}
			{/*    flex items-center justify-center */}
			{/*    ${showThumbnails && viewMode === 'slider' ? 'p-4 h-[8.5rem]' : 'p-0 max-h-0 overflow-hidden'}*/}
			{/* `}*/}
			{/*>*/}
			{/*	{showThumbnails && viewMode === 'slider' && (*/}
			{/*		<>*/}
			{/*			<button*/}
			{/*				onClick={() => scrollThumbnails('left')}*/}
			{/*				className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 bg-black bg-opacity-70 hover:bg-opacity-90 rounded-full transition-all"*/}
			{/*				aria-label="Scroll thumbnails left"*/}
			{/*			>*/}
			{/*				<ChevronLeft className="text-white font-custom-medium" size={20}/>*/}
			{/*			</button>*/}

			{/*			<div*/}
			{/*				ref={thumbnailContainerRef}*/}
			{/*				className="flex gap-3 overflow-x-auto no-scrollbar py-2 px-[calc(50%-3rem)]"*/}
			{/*				style={{scrollBehavior: 'smooth'}}*/}
			{/*			>*/}
			{/*				{media.map((item, index) => (*/}
			{/*					<div*/}
			{/*						key={item._id}*/}
			{/*						id={`lightbox-thumb-${item._id}`}*/}
			{/*						onClick={() => handleThumbnailClick(index)}*/}
			{/*						className={`relative aspect-auto min-h-[5rem] h-[5rem] max-h-[5rem]*/}
			{/*                           flex-shrink-0 rounded-lg overflow-hidden cursor-pointer transition-all*/}
			{/*                           ${index === currentIndex*/}
			{/*							? 'ring-4 ring-blue-500 opacity-100'*/}
			{/*							: 'opacity-60 hover:opacity-100'}*/}
			{/*                        `}*/}
			{/*					>*/}
			{/*						<img*/}
			{/*							src={item.thumbnailUrl}*/}
			{/*							alt={eventDetails.title}*/}
			{/*							className={`w-full h-full object-cover ${index !== currentIndex && 'transition-transform duration-300 group-hover:scale-110'}`}*/}
			{/*						/>*/}

			{/*						{mediaType === 'video' &&*/}
			{/*							(*/}
			{/*								index === currentIndex*/}
			{/*									? (*/}
			{/*										<div*/}
			{/*											className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">*/}
			{/*											<Play*/}
			{/*												className="text-white font-custom-medium drop-shadow-lg"*/}
			{/*												size={24}*/}
			{/*											/>*/}
			{/*										</div>*/}
			{/*									) : (*/}
			{/*										<div*/}
			{/*											className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300">*/}
			{/*											<Play*/}
			{/*												className="text-white font-custom-medium opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-110"*/}
			{/*												size={24}*/}
			{/*											/>*/}
			{/*										</div>*/}
			{/*									)*/}
			{/*							)*/}
			{/*						}*/}
			{/*					</div>*/}
			{/*				))}*/}
			{/*			</div>*/}

			{/*			<button*/}
			{/*				onClick={() => scrollThumbnails('right')}*/}
			{/*				className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 bg-black bg-opacity-70 hover:bg-opacity-90 rounded-full transition-all"*/}
			{/*				aria-label="Scroll thumbnails right"*/}
			{/*			>*/}
			{/*				<ChevronRight className="text-white font-custom-medium" size={20}/>*/}
			{/*			</button>*/}
			{/*		</>*/}
			{/*	)}*/}
			{/*</div>*/}

			{/* Thumbnails Section */}
			<div
				className={`bg-black bg-opacity-50 relative transition-all duration-300 ease-in-out flex items-center justify-center
					${showThumbnails && viewMode === 'slider'
					? 'p-4 h-[8.5rem]'
					: 'p-0 max-h-0 overflow-hidden'}
				`}
			>
				{showThumbnails && viewMode === 'slider' && (
					<>
						<button
							onClick={() => scrollThumbnails('left')}
							className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 bg-black bg-opacity-70 hover:bg-opacity-90 rounded-full transition-all"
						>
							<ChevronLeft className="text-white font-custom-medium" size={20}/>
						</button>

						<div
							ref={thumbnailContainerRef}
							className="flex gap-3 overflow-x-auto no-scrollbar py-2 px-[calc(50%-3rem)]"
							style={{scrollBehavior: 'smooth'}}
						>
							{media.map((item, index) => (
								<div
									key={item._id}
									id={`lightbox-thumb-${item._id}`}
									onClick={() => handleThumbnailClick(index)}
									// Added 'group' here to enable group-hover on children
									className={`relative aspect-auto min-h-[5rem] h-[5rem] max-h-[5rem]
										flex-shrink-0 rounded-lg overflow-hidden cursor-pointer transition-all group
										${index === currentIndex
										? 'ring-4 ring-blue-500 opacity-100'
										: 'opacity-60 hover:opacity-100'}`}
								>
									<img
										src={item.thumbnailUrl}
										alt={eventDetails.title}
										className={`w-full h-full object-cover ${index !== currentIndex && 'transition-transform duration-300 group-hover:scale-110'}`}
									/>

									{/* UPDATED: Play button only appears on hover (opacity-0 -> opacity-100) */}
									{mediaType === 'video' && (
										index === currentIndex
											? (
												<div
													className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
													<Play
														className="text-white font-custom-medium drop-shadow-lg"
														size={24}
													/>
												</div>
											) : (
												<div
													className="absolute inset-0
														flex items-center justify-center
														transition-all duration-300
														bg-black bg-opacity-0 group-hover:bg-opacity-40"
												>
													<Play
														className={`text-white font-custom-medium transition-all duration-300
															drop-shadow-lg opacity-0 group-hover:opacity-100
															transform scale-75 group-hover:scale-110`}
														size={24}
													/>
												</div>
											)
									)}
								</div>
							))}
						</div>

						<button
							onClick={() => scrollThumbnails('right')}
							className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 bg-black
							bg-opacity-70 hover:bg-opacity-90 rounded-full transition-all"
						>
							<ChevronRight className="text-white font-custom-medium" size={20}/>
						</button>
					</>
				)}
			</div>

			{/* Mobile Floating Menu */}
			{viewMode === 'slider' && (
				<div className="sm:hidden">
					<button
						onClick={() => setShowMobileMenu(!showMobileMenu)}
						className={`fixed ${showThumbnails ? 'bottom-36' : 'bottom-12'} left-1/2 -translate-x-1/2 z-20 p-3 
							bg-black bg-opacity-70 hover:bg-opacity-90 rounded-full transition-all`}
						aria-label="Open Menu"
					>
						<Menu className="text-white font-custom-medium" size={24}/>
					</button>

					{showMobileMenu && (
						<div
							className={`fixed ${showThumbnails ? 'bottom-48' : 'bottom-28'} left-1/2 -translate-x-1/2 z-20
							bg-black bg-opacity-90 rounded-2xl p-3 flex flex-col gap-2 shadow-2xl`}
						>
							{!isCurrentVideo && (
								<>
									<div className="flex items-center gap-2 pb-2 border-b border-gray-700">
										<button
											onClick={handleZoomOut}
											disabled={zoom <= 0.5}
											className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
											aria-label="Zoom Out"
										>
											<ZoomOut className="text-white font-custom-medium" size={20}/>
										</button>

										<span className="text-white font-custom-medium text-sm min-w-16 text-center">
		                                   {Math.round(zoom * 100)}%
		                               </span>

										<button
											onClick={handleZoomIn}
											disabled={zoom >= 4}
											className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
											aria-label="Zoom In"
										>
											<ZoomIn className="text-white font-custom-medium" size={20}/>
										</button>
									</div>

									<div className="flex items-center gap-2">
										<button
											onClick={handleRotate}
											className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors flex-1"
											title="Rotate"
											aria-label="Rotate"
										>
											<RotateCw className="text-white font-custom-medium mx-auto" size={20}/>
										</button>

										<button
											onClick={handleDownload}
											className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors flex-1"
											title="Download"
											aria-label="Download"
										>
											<Download className="text-white font-custom-medium mx-auto" size={20}/>
										</button>
									</div>

									<div className="border-t border-gray-700 pt-2">
										<button
											onClick={() => {
												setShowThumbnails(!showThumbnails);
												setShowMobileMenu(false);
											}}
											className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors w-full flex items-center justify-center gap-2"
										>
											{showThumbnails ? (
												<>
													<EyeOff className="text-white font-custom-medium" size={20}/>
													<span className="text-white font-custom-medium text-sm">Hide Thumbnails</span>
												</>
											) : (
												<>
													<Eye className="text-white font-custom-medium" size={20}/>
													<span className="text-white font-custom-medium text-sm">Show Thumbnails</span>
												</>
											)}
										</button>
									</div>
								</>
							)}
							{isCurrentVideo && (
								<button
									onClick={() => {
										setShowThumbnails(!showThumbnails);
										setShowMobileMenu(false);
									}}
									className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors w-full flex items-center justify-center gap-2"
								>
									{showThumbnails ? (
										<>
											<EyeOff className="text-white font-custom-medium" size={20}/>
											<span
												className="text-white font-custom-medium text-sm">Hide Thumbnails</span>
										</>
									) : (
										<>
											<Eye className="text-white font-custom-medium" size={20}/>
											<span
												className="text-white font-custom-medium text-sm">Show Thumbnails</span>
										</>
									)}
								</button>
							)}
						</div>
					)}
				</div>
			)}
		</div>
	);
};